import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal'
import { ApplicationRef, DOCUMENT, Injectable, Injector, inject, signal } from '@angular/core'

import { Subject } from 'rxjs'

import { FlxNotificationsComponent } from '../notifications.component'
import { ExtendedNotification, NOTIFICATIONS_DATA } from '../notifications.model'

const HOST_CLASS = 'flx-notifications-container'
const MAX_VISIBLE = 3

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  #document = inject(DOCUMENT)
  #applicationRef = inject(ApplicationRef)
  #injector = inject(Injector)
  #portalOutlet?: DomPortalOutlet

  #correlationIds: string[] = []
  #correlatedNotification = new Subject<ExtendedNotification>()
  #suppressors = new Set<string>()
  #queue: ExtendedNotification[] = []
  #paused = false

  activeNotifications = signal<ExtendedNotification[]>([])

  correlatedNotification$ = this.#correlatedNotification.asObservable()

  addCorrelationId(correlationId: string): void {
    this.#correlationIds = [...this.#correlationIds, correlationId]
  }

  removeCorrelationId(correlationId: string): void {
    this.#correlationIds = this.#correlationIds.filter((id) => id !== correlationId)
  }

  addToastSuppressor(source: string): void {
    this.#suppressors.add(source)
  }

  removeToastSuppressor(source: string): void {
    this.#suppressors.delete(source)
  }

  push(notification: ExtendedNotification): void {
    const { id, correlationId } = notification

    // An export reports when it starts and again when it finishes, under one id, so a quick one would
    // show both. The newer report makes the older one stale, wherever it is held.
    this.#queue = this.#queue.filter((queued) => queued.id !== id)
    this.dismiss(id)

    if (correlationId && this.#correlationIds.includes(correlationId)) {
      this.removeCorrelationId(correlationId)
      this.#correlatedNotification.next(notification)
      return
    }

    if (this.#suppressors.size) {
      return
    }

    this.#attachHost()

    if (this.#paused || this.activeNotifications().length >= MAX_VISIBLE) {
      this.#queue = [...this.#queue, notification]
      return
    }

    this.activeNotifications.update((notifications) => [...notifications, notification])
  }

  dismiss(notificationId: string): void {
    this.activeNotifications.update((notifications) =>
      notifications.filter((notification) => notification.id !== notificationId)
    )

    this.#fillFreeSlots()
  }

  dismissAll(): void {
    this.#queue = []
    this.activeNotifications.set([])
  }

  setPaused(paused: boolean): void {
    this.#paused = paused
    this.#fillFreeSlots()
  }

  #fillFreeSlots(): void {
    const freeSlots = MAX_VISIBLE - this.activeNotifications().length

    // A paused stack holds its arrivals so nothing moves under the pointer; the queue drains once it resumes.
    if (this.#paused || freeSlots <= 0 || !this.#queue.length) {
      return
    }

    const queuedNotifications = this.#queue.splice(0, freeSlots)
    this.activeNotifications.update((notifications) => [...notifications, ...queuedNotifications])
  }

  #attachHost(): void {
    if (this.#portalOutlet) {
      return
    }

    const hostElement = this.#document.createElement('div')
    hostElement.classList.add(HOST_CLASS)
    this.#document.body.appendChild(hostElement)
    this.#portalOutlet = new DomPortalOutlet(hostElement, this.#applicationRef)

    this.#portalOutlet.attach(
      new ComponentPortal(FlxNotificationsComponent, null, this.#createHostInjector())
    )
  }

  #createHostInjector(): Injector {
    return Injector.create({
      parent: this.#injector,
      providers: [
        {
          provide: NOTIFICATIONS_DATA,
          useValue: {
            notifications: this.activeNotifications.asReadonly(),
            dismiss: (id: string): void => this.dismiss(id),
            setPaused: (paused: boolean): void => this.setPaused(paused),
          },
        },
      ],
    })
  }
}
